import { ScriptLoader } from '@thiememeulenhoff/js-sdk';
import { property, query, state } from 'lit/decorators.js';
import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { MEDIA_EVENT } from '@thiememeulenhoff/types';
import {
  Bluebillywig,
  BluebillywigPlayer,
  BluebillywigPlayerState,
  BluebillywigPlayerStateEnum,
} from './media-player.type';

declare global {
  interface Window {
    bluebillywig: Bluebillywig | null;
  }
}

export class MediaPlayerBaseComponent extends TmElement {
  @property({ type: String })
  override id!: string;

  @property({ attribute: 'use-placeholder' })
  usePlaceholder = false;

  @state()
  protected player!: BluebillywigPlayer;

  @state()
  currentPlayerState!: BluebillywigPlayerState;

  @state()
  initiated = false;

  @state()
  playing = false;

  @state()
  paused = false;

  @state()
  idle = false;

  @state()
  loading = false;

  @state()
  classes = {
    playing: this.playing,
    paused: this.paused,
    idle: this.idle,
  };

  @state()
  playerLoaded = false;

  @query('.player-container')
  playerContainer!: HTMLDivElement;

  bbwPlayout = '';

  dataApiUrl = 'https://thiememeulenhoff.bbvms.com/p/default/c/{vmsId}.json';

  scriptSrc = 'https://thiememeulenhoff.bbvms.com/launchpad/';

  playAfterInit = false;

  constructor() {
    super();

    if (ScriptLoader.scriptExists(this.scriptSrc)) {
      this.playerLoaded = true;
    }

    ScriptLoader.loadScript({
      src: this.scriptSrc,
      callback: () => {
        this.playerLoaded = true;
      },
    });
  }

  override connectedCallback() {
    super.connectedCallback();

    this.playAfterInit = this.usePlaceholder;
  }

  override disconnectedCallback() {
    this.#destroyPlayer();
  }

  async getJson() {
    const response = await fetch(this.dataApiUrl.replace('{vmsId}', this.id));
    return response.json();
  }

  protected togglePlayState() {
    if (!this.initiated) {
      throw Error(
        `Player is not initiated, probably because the Bluebillywig script failed to load.`,
      );
    }

    if (this.player.getState() === BluebillywigPlayerStateEnum.Playing) {
      this.player.pause('pause');
    } else {
      this.player.play('play');
    }
  }

  initPlayer() {
    const loadInterval = setInterval(() => {
      if (window.bluebillywig) {
        this.usePlaceholder = false;
        this.#loadPlayer();
        this.initiated = true;
        this.player.on('play', () => this.#onPlay());
        this.player.on('pause', () => this.#onPause());
        this.player.on('statechange', () => this.#onStateChange());
        clearInterval(loadInterval);
      }
    }, 100);
  }

  #loadPlayer() {
    const url = `https://thiememeulenhoff.bbvms.com/p/${this.bbwPlayout}/c/${this.id}.json`;
    const bbw = window.bluebillywig as Bluebillywig;

    this.player = new bbw.Player(url, {
      target: this.playerContainer,
      autoPlay: true,
    });
  }

  #destroyPlayer() {
    if (this.player && this.player.destruct) {
      this.player.destruct();
    }
  }

  #pauseOtherPlayers() {
    const bbw = window.bluebillywig as Bluebillywig;
    bbw.players.forEach((player: BluebillywigPlayer) => {
      if (player === this.player) return;
      player.pause('pause');
    });
  }

  #onStateChange() {
    this.currentPlayerState = this.player.getState();

    this.loading =
      this.currentPlayerState === BluebillywigPlayerStateEnum.Loading;
    this.playing =
      this.currentPlayerState === BluebillywigPlayerStateEnum.Playing;
    this.paused =
      this.currentPlayerState === BluebillywigPlayerStateEnum.Paused;
    this.idle = this.currentPlayerState === BluebillywigPlayerStateEnum.Idle;

    const event = new Event(this.currentPlayerState);
    this.dispatchLocalEvent({
      eventName: this.currentPlayerState,
      detail: event,
    });

    // If this instance used a placeholder we want to start playing the video after it has been initialized
    if (this.playAfterInit) {
      // If the state is 'paused' we start playing (this is the initial state)
      if (this.currentPlayerState === BluebillywigPlayerStateEnum.Paused) {
        this.player.play('play');
        this.playAfterInit = false;
      }
    }

    const itempropElements = this.shadowRoot?.querySelectorAll('[itemprop]');

    if (itempropElements) {
      itempropElements.forEach((element) => {
        const itemprop = element.getAttribute('itemprop') as 'video' | 'audio';
        const action = {
          loading: false,
          playing: 'started',
          paused: 'paused',
          idle: 'ended',
        }[this.currentPlayerState] as
          'paused' | 'started' | 'ended' | 'resumed';

        if (action) {
          this.dispatchActivityEvent(MEDIA_EVENT, {
            action,
            type: itemprop,
            target: this.id,
          });
        }
      });
    }
  }

  #onPlay() {
    this.#pauseOtherPlayers();
  }

  #onPause(): BluebillywigPlayerState {
    // Just returning the state for now
    return this.player.getState();
  }
}
